// Browser history for the public wizard: one entry per step change, so the system back gesture (Android, Safari's
// swipe, the browser's button) goes to the previous step and only leaves the wizard from the first one.
//
// The wizard's base entry is the one the landing pushed to open it (`?cotizar=1`, see Index). Step entries stack on top
// of it with the same URL. They live inside the router's `usr` state (the location.state slot) next to its own
// `key`/`idx`, so React Router sees them as the same location and ignores them.

interface WizardEntry {
  /** The step this entry shows */
  step?: number;
  /** The step Atrás returns to from this entry */
  prev?: number;
  /** Step entries stacked above the base entry */
  depth?: number;
}

const readEntry = (): WizardEntry => window.history.state?.usr?.wizard ?? {};

const writeEntry = (method: 'pushState' | 'replaceState', entry: WizardEntry) => {
  const state = window.history.state ?? {};
  window.history[method]({ ...state, usr: { ...state.usr, wizard: entry } }, '');
};

/** Step entries above the base entry; closing the wizard has to pop all of them (see Index) */
export const wizardHistoryDepth = () => readEntry().depth ?? 0;

/** The step of the current entry, if it is one of ours */
export const wizardHistoryStep = () => readEntry().step;

/** Where Atrás goes from this entry: the step the user came from */
export const wizardHistoryPrev = () => readEntry().prev;

/** Marks the base entry with the step the wizard opened on */
export const markWizardEntry = (step: number) => writeEntry('replaceState', { ...readEntry(), step });

/** A step change: a new entry on top, remembering where to go back to */
export const pushWizardStep = (step: number, from: number) =>
  writeEntry('pushState', { step, prev: from, depth: wizardHistoryDepth() + 1 });

/** Atrás with no entry to pop (the wizard resumed mid-flow): the current entry now shows the previous step */
export const replaceWizardStep = (step: number) => writeEntry('replaceState', { ...readEntry(), step });
